<template>
  <div class="component">
    <Badge
      v-if="isConnected || newConnectionComplete"
      class="badge"
      status="success"
      iconBefore="fa fa-check"
    >
      Connected
    </Badge>

    <Badge
      v-else-if="connectionInProgress"
      class="badge"
      status="info"
      iconBefore="fa fa-spinner fa-spin"
    >
      Connecting
    </Badge>

    <Badge
      v-else-if="isConnected === undefined"
      class="badge"
      iconBefore="fa fa-spinner fa-spin"
    >
      Checking Connection
    </Badge>

    <div v-else>
      <!-- For Salesforce only, show modal before connecting. -->
      <Button
        v-if="app.name === 'Salesforce'"
        @click="showSalesforcePreConnect = true"
        iconBefore="fa fa-plug"
        spacing="tight"
      >
        Connect
      </Button>

      <Button
        v-else
        :href="authURL?.href"
        target="_blank"
        iconBefore="fa fa-plug"
        spacing="tight"
      >
        Connect
      </Button>
    </div>

    <!-- Salesforce PreConnect Modal -->
    <!-- To make sure user has installed the YS app in their Salesforce org -->
    <StakeModal v-model="showSalesforcePreConnect">
      <SalesforcePreConnect :url="authURL?.href" />
    </StakeModal>

    <Button
      v-if="isConnected"
      @click="confirmDisconnect"
      iconBefore="fa fa-unlink"
      spacing="tight"
      variant="danger"
    >
      Disconnect
    </Button>
  </div>
</template>

<script>
import StakeModal from '@stakeui/StakeModal.vue';
import SalesforcePreConnect from '@features/oauth/components/SalesforcePreConnect.vue';
import {
  Badge,
  Button,
  Buttons,
} from '@stakeui';

export default {
  name: 'OAuthConnect',
  components: {
    StakeModal,
    SalesforcePreConnect,
    Badge,
    Button,
    Buttons,
  },
  props: {
    app: {
      type: Object,
      required: true,
    },
    isConnected: {
      type: Boolean,
      default: undefined,
    },
    code: {
      type: String,
      default: null,
    },
    authorizedApp: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      connectionInProgress: null,
      newConnectionComplete: null,
      showSalesforcePreConnect: false,
    };
  },
  computed: { /*
    Creates a new property
    Updates when any dependant property changes */
    headers() {
      return {
        'X-CSRFToken': this.$root.$data.csrfToken,
        'Content-Type': 'application/json',
      };
    },
    redirectBase() {
      if (this.app.redirect_url) return this.app.redirect_url;
      return `${window.location.origin}/ui/advisor/integrations`;
    },
    // The authState is passed to the provider as a url param, and then returned on redirect to YourStake.
    // This allows us to know which app the user was trying to connect to
    // You can add more data here if needed
    authState() {
      return {
        app: this.app.name,
      };
    },
    mergedScopes() {
      const required = this.app.scope ? this.app.scope.split(' ') : [];
      const optional = this.app.optional_scope ? this.app.optional_scope.split(' ') : [];
      
      const unique = new Set([...required, ...optional]);
      return Array.from(unique).join(' ');
    },
    authURL() {
      if (!this.app?.auth_url) return null;
      const url = new URL(this.app.auth_url);

      const params = {
        response_type: 'code',
        client_id: this.app.client_id,
        redirect_uri: this.redirectBase,
        state: btoa(JSON.stringify(this.authState)),
      };
      if (this.mergedScopes) params.scope = this.mergedScopes;

      // Google will not provide the refresh token unless we do this
      if (this.app.name.toLowerCase().includes('google')) {
        params.access_type = 'offline';
      }

      url.search = new URLSearchParams(params).toString();
      return url;
    },
  },
  watch: { /*
      Watches an existing property
      Only runs when the watched property changes */
  },
  beforeCreate() {},
  created() {
    if (this.code && this.authorizedApp && this.app.name === this.authorizedApp) {
      this.connect();
    }
  },
  mounted() {},
  updated() {},
  methods: {
    async connect() {
      this.connectionInProgress = true;
      const defaultErrorMessage = [
        `Something went wrong while trying to connect to ${this.app.name}.`,
        'Please try again or contact support.',
      ].join(' ');

      try {
        const res = await fetch('/api/v1/oauth/connect/', {
          method: 'POST',
          headers: this.headers,
          body: JSON.stringify({ code: this.code, app: this.app.name }),
        });
        const data = await res.json();

        if (!res.ok || !data.connected) {
          console.error(`Error connecting to ${this.app.name}`, data);
          console.log(data);
          this.connectionInProgress = false;
          alert(data.message || defaultErrorMessage);
          return;
        }

        this.newConnectionComplete = true;
        this.$emit('update-connection', true);
        //
      } catch (error) {
        // This is a fail safe for unexpected errors
        // If we are here, it's most likely not the user's fault.
        console.error(`Error connecting to ${this.app.name}`, error);
        alert(defaultErrorMessage);
      } finally {
        this.connectionInProgress = false;
      }
    },
    confirmDisconnect() {
      if (window.confirm('Are you sure you want to disconnect?')) {
        this.disconnect();
      }
    },
    async disconnect() {
      try {
        const res = await fetch('/api/v1/oauth/disconnect/', {
          method: 'POST',
          headers: this.headers,
          body: JSON.stringify({ app_name: this.app.name }),
        });
        const data = await res.json();
        if (res.ok) {
          this.$emit('update-connection', false);
          this.newConnectionComplete = false;
          alert(data.message);
        } else {
          alert(data.error);
        }
      } catch (error) {
        console.error('Error disconnecting:', error);
      }
    },
  },
};
</script>

<style scoped>
.component {
  display: flex;
  align-items: end;
  gap: 1em;
  flex-grow: 1;
  justify-content: end;
  flex-direction: column;
}

.badge {
  margin-block-end: auto;
}
</style>
